import type {
  SignalingMessage,
  SignalingMessageType,
  JoinPayload,
  OfferPayload,
  AnswerPayload,
  IceCandidatePayload,
  HaveSegmentPayload,
  SegmentMapPayload,
  PeerStatsPayload,
} from '@ohmyprime/p2p-shared';

type MessageHandler = (msg: SignalingMessage) => void;

const HEARTBEAT_INTERVAL_MS = 10_000;

/** Tracker close codes that mean "do not just reconnect". */
export const CLOSE_UNAUTHORIZED = 4401;
export const CLOSE_UNSUPPORTED = 4406;
export const CLOSE_JOIN_TIMEOUT = 4408;
export const CLOSE_REPLACED = 4409;
export const CLOSE_STREAM_ENDED = 4410;

export interface SignalingRejection {
  code: number;
  reason: string;
  /** True when the client reconnects on its own (fresh-token retry, or legacy 4401 backoff). */
  willRetry: boolean;
}

export interface SignalingClientOptions {
  /** Called before every (re)connect; its token replaces the static one. Honor signal for HTTP cancellation. */
  getToken?: (signal: AbortSignal) => Promise<{ token: string; ts: number }>;
  /** Called when the tracker closes the socket with 4401/4406/4408/4409/4410. */
  onRejected?: (info: SignalingRejection) => void;
}
const INITIAL_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;

/**
 * WebSocket client for the signaling server.
 * Handles reconnection with exponential backoff and periodic heartbeats.
 */
function isValidSignalingMessage(obj: unknown): obj is { type: string; from: string; payload: unknown } {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof (obj as Record<string, unknown>)['type'] === 'string' &&
    typeof (obj as Record<string, unknown>)['from'] === 'string'
  );
}

export class SignalingClient {
  private readonly signalingUrl: string;
  private readonly peerId: string;
  private token: string | undefined;
  private tokenTs: number | undefined;
  private readonly options: SignalingClientOptions;
  /** Set when a rejection stopped auto-reconnect; connect() clears it. */
  private stopped = false;
  /** A 4401 already triggered one fresh-token retry; cleared once the tracker talks to us. */
  private retriedUnauthorized = false;
  /** Guards against a stale getToken() resolving after a newer open or destroy. */
  private openGeneration = 0;
  private tokenAbort: AbortController | null = null;
  private readonly tokenWork = new Set<Promise<void>>();
  private closePromise: Promise<void> | null = null;
  private readonly tokenStreamId: string | undefined;
  private readonly role: string | undefined;
  private readonly sessionId: string | undefined;

  private ws: WebSocket | null = null;
  private destroyed = false;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  private readonly handlers: Map<SignalingMessageType, MessageHandler[]> = new Map();
  private readonly onConnectHandlers: Array<() => void> = [];
  private readonly onDisconnectHandlers: Array<() => void> = [];

  constructor(
    url: string,
    peerId: string,
    token?: string,
    tokenTs?: number,
    tokenStreamId?: string,
    role?: string,
    sessionId?: string,
    options: SignalingClientOptions = {},
  ) {
    this.signalingUrl = url;
    this.peerId = peerId;
    this.token = token;
    this.tokenTs = tokenTs;
    this.tokenStreamId = tokenStreamId;
    this.role = role;
    this.sessionId = sessionId;
    this.options = options;
  }

  private buildUrl(): string {
    const base = this.signalingUrl;
    const token = this.token;
    const ts = this.tokenTs;
    const streamId = this.tokenStreamId;
    const params: string[] = [];
    if (token && ts && streamId) {
      params.push(`token=${token}`, `stream=${encodeURIComponent(streamId)}`, `ts=${ts}`);
    }
    if (this.role) params.push(`role=${encodeURIComponent(this.role)}`);
    if (this.sessionId) params.push(`session=${encodeURIComponent(this.sessionId)}`);
    if (params.length === 0) return base;
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}${params.join('&')}`;
  }

  connect(): void {
    if (this.destroyed) return;
    if (this.ws) return;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopped = false;
    this.retriedUnauthorized = false;
    this.openSocket();
  }

  /** True after a rejection that stopped auto-reconnect (see SignalingRejection). */
  isStopped(): boolean {
    return this.stopped;
  }

  /** Token providers must honor the supplied signal; close waits for their actual settlement. */
  close(): Promise<void> {
    if (this.closePromise) return this.closePromise;
    this.destroy();
    this.closePromise = Promise.allSettled([...this.tokenWork]).then(() => {});
    return this.closePromise;
  }
  destroy(): void {
    this.destroyed = true;
    this.tokenAbort?.abort(); this.tokenAbort = null;
    this.openGeneration++;
    this.handlers.clear(); this.onConnectHandlers.length = 0; this.onDisconnectHandlers.length = 0;
    this.clearTimers();
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
  }

  send(type: SignalingMessageType, to: string | undefined, payload: unknown): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const msg: SignalingMessage = {
      type,
      from: this.peerId,
      to,
      payload,
      timestamp: Date.now(),
    };
    this.ws.send(JSON.stringify(msg));
  }

  sendJoin(payload: JoinPayload): void {
    this.send('join', undefined, payload);
  }

  sendLeave(): void {
    this.send('leave', undefined, {});
  }

  sendOffer(to: string, payload: OfferPayload): void {
    this.send('offer', to, payload);
  }

  sendAnswer(to: string, payload: AnswerPayload): void {
    this.send('answer', to, payload);
  }

  sendIceCandidate(to: string, payload: IceCandidatePayload): void {
    this.send('ice-candidate', to, payload);
  }

  sendHaveSegment(payload: HaveSegmentPayload): void {
    this.send('have-segment', undefined, payload);
  }

  sendSegmentMap(payload: SegmentMapPayload): void {
    this.send('segment-map', undefined, payload);
  }

  sendPeerStats(payload: PeerStatsPayload): void {
    this.send('peer-stats', undefined, payload);
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  on(type: SignalingMessageType, handler: MessageHandler): void {
    const list = this.handlers.get(type) ?? [];
    list.push(handler);
    this.handlers.set(type, list);
  }

  off(type: SignalingMessageType, handler: MessageHandler): void {
    const list = this.handlers.get(type);
    if (!list) return;
    const idx = list.indexOf(handler);
    if (idx !== -1) list.splice(idx, 1);
  }

  onConnect(cb: () => void): void {
    this.onConnectHandlers.push(cb);
  }

  offConnect(cb: () => void): void {
    const idx = this.onConnectHandlers.indexOf(cb);
    if (idx !== -1) this.onConnectHandlers.splice(idx, 1);
  }

  onDisconnect(cb: () => void): void {
    this.onDisconnectHandlers.push(cb);
  }

  private openSocket(): void {
    if (this.destroyed) return;
    const generation = ++this.openGeneration;
    this.tokenAbort?.abort(); this.tokenAbort = null;
    const getToken = this.options.getToken;
    if (!getToken) {
      this.createSocket();
      return;
    }
    const abort = new AbortController();
    this.tokenAbort = abort;
    // Invoke synchronously as before; defer only a thrown provider failure into its rejection path.
    let token: Promise<{token:string;ts:number}>;
    try { token = getToken(abort.signal); } catch (error) { token = Promise.reject(error); }
    const work = token.then(
      ({ token, ts }) => {
        if (this.destroyed || generation !== this.openGeneration) return;
        this.token = token;
        this.tokenTs = ts;
        this.createSocket();
      },
      () => {
        if (this.destroyed || generation !== this.openGeneration) return;
        this.scheduleReconnect();
      },
    );
    this.tokenWork.add(work);
    void work.then(() => this.tokenWork.delete(work), () => this.tokenWork.delete(work));
  }

  private createSocket(): void {
    const ws = new WebSocket(this.buildUrl());
    ws.binaryType = 'arraybuffer';
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectAttempt = 0;
      this.startHeartbeat();
      for (const cb of this.onConnectHandlers) cb();
    };

    ws.onmessage = (event: MessageEvent) => {
      // The tracker only talks to accepted sockets: the token worked.
      this.retriedUnauthorized = false;
      let msg: unknown;
      try {
        msg = JSON.parse(event.data as string);
      } catch {
        return; // ignore unparseable
      }
      if (!isValidSignalingMessage(msg)) return; // ignore malformed
      const list = this.handlers.get(msg.type as SignalingMessageType);
      if (list) {
        for (const h of list) h(msg as SignalingMessage);
      }
    };

    ws.onerror = () => {
      // Will be followed by onclose
    };

    ws.onclose = (event: CloseEvent) => {
      this.ws = null;
      this.stopHeartbeat();
      for (const cb of this.onDisconnectHandlers) cb();
      if (this.destroyed) return;
      this.handleClose(event.code, event.reason ?? '');
    };
  }

  /**
   * 4401: with getToken, one immediate retry with a fresh token, then stop; without
   * getToken, the legacy backoff retry. 4406/4408/4409/4410: stop. Anything else: backoff.
   */
  private handleClose(code: number, reason: string): void {
    const reject = (willRetry: boolean) => this.options.onRejected?.({ code, reason, willRetry });
    if (code === CLOSE_UNAUTHORIZED) {
      if (!this.options.getToken) {
        reject(true);
        this.scheduleReconnect();
        return;
      }
      if (!this.retriedUnauthorized) {
        this.retriedUnauthorized = true;
        reject(true);
        this.openSocket();
        return;
      }
      this.stopped = true;
      reject(false);
      return;
    }
    if (code === CLOSE_UNSUPPORTED || code === CLOSE_JOIN_TIMEOUT || code === CLOSE_REPLACED || code === CLOSE_STREAM_ENDED) {
      this.stopped = true;
      reject(false);
      return;
    }
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    const backoff = Math.min(
      INITIAL_BACKOFF_MS * Math.pow(2, this.reconnectAttempt),
      MAX_BACKOFF_MS,
    );
    this.reconnectAttempt++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, backoff);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // Send a ping-style heartbeat reusing the have-segment slot
        // as a lightweight keep-alive; servers can ignore unknown payloads.
        this.ws.send(JSON.stringify({ type: 'ping', from: this.peerId, timestamp: Date.now() }));
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private clearTimers(): void {
    this.stopHeartbeat();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
